type UseTypewriterReturn = {
    text: Readonly<Ref<string>>;
};

const TYPE_DELAY_MS = 55;
const ERASE_DELAY_MS = 25;
const HOLD_DELAY_MS = 2000;
const NEXT_PHRASE_DELAY_MS = 400;

export const useTypewriter = (phrases: MaybeRefOrGetter<string[]>, isActive: MaybeRefOrGetter<boolean>): UseTypewriterReturn => {
    const text = ref<string>(toValue(phrases)[0] ?? '');
    let phraseIndex = 0;
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;

    const clearTimer = (): void => {
        clearTimeout(timer);
        timer = undefined;
    };

    // By code point, not UTF-16 unit, so a character outside the BMP (an emoji) never shows half-typed.
    const type = (): void => {
        const characters = Array.from(toValue(phrases)[phraseIndex] ?? '');
        const typedCount = Array.from(text.value).length;

        if (typedCount < characters.length) {
            text.value = characters.slice(0, typedCount + 1).join('');
            timer = setTimeout(type, TYPE_DELAY_MS);

            return;
        }

        timer = setTimeout(erase, HOLD_DELAY_MS);
    };

    const erase = (): void => {
        if (text.value.length > 0) {
            text.value = Array.from(text.value).slice(0, -1).join('');
            timer = setTimeout(erase, ERASE_DELAY_MS);

            return;
        }

        phraseIndex = (phraseIndex + 1) % Math.max(toValue(phrases).length, 1);
        timer = setTimeout(type, NEXT_PHRASE_DELAY_MS);
    };

    watch(
        () => toValue(isActive),
        active => {
            clearTimer();

            if (active) type();
        },
        { immediate: true }
    );

    watch(
        () => toValue(phrases),
        newPhrases => {
            phraseIndex = 0;
            text.value = newPhrases[0] ?? '';

            if (timer) {
                clearTimer();
                type();
            }
        }
    );

    tryOnScopeDispose(clearTimer);

    return { text };
};
